import React, { memo, ReactNode } from 'react'
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CitationToken } from 'components/messages/citations/CitationToken'
import { CitationSource } from 'types/chat-message'
import { MarkdownLink } from 'components/ui/MarkdownLink'

type MarkdownTextProps = {
  text: string
  className?: string
  sources?: Record<string, CitationSource> | null
}

// [N] remote source, [img:N] image, [att:N] user-uploaded attachment.
const CITATION_SPLIT_REGEX = /\[(img:|att:)?(\d+)\]/g

// react-markdown strips tel: by default (not in its safe-protocol list), which would
// blank the href and open the widget itself. Keep tel: as-is, sanitize the rest.
const urlTransform = (url: string) => (/^tel:/i.test(url) ? url : defaultUrlTransform(url))

function processCitationChildren(children: ReactNode, sources?: Record<string, CitationSource> | null): ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      const parts = child.split(CITATION_SPLIT_REGEX)
      if (parts.length <= 1) return child
      const result: ReactNode[] = []
      for (let i = 0; i < parts.length; i += 3) {
        const text = parts[i]
        if (text) result.push(text)

        const flag = parts[i + 1]
        const num = parts[i + 2]
        if (num === undefined) continue

        result.push(<CitationToken key={`cite-${i}`} flag={flag} num={num} sources={sources} />)
      }
      return result
    }
    if (React.isValidElement<{ children?: ReactNode }>(child) && child.props?.children) {
      return React.cloneElement(child, {}, processCitationChildren(child.props.children, sources))
    }
    return child
  })
}

const MarkdownTextComponent = ({ text, className, sources }: MarkdownTextProps) => {
  const pc = (children: unknown) => processCitationChildren(children as ReactNode, sources)

  const components: Components = {
    h1: ({ children, ...props }) => (
      <h1 className="tw-text-3xl tw-font-bold tw-mt-6 tw-mb-4" {...props}>
        {pc(children)}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className="tw-text-2xl tw-font-bold tw-mt-5 tw-mb-3" {...props}>
        {pc(children)}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="tw-text-xl tw-font-bold tw-mt-4 tw-mb-2" {...props}>
        {pc(children)}
      </h3>
    ),

    ul: ({ children, ...props }) => (
      <ul className="tw-list-disc tw-pl-4" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="tw-list-decimal tw-pl-4" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li {...props} style={{ paddingInlineStart: 0 }}>
        {pc(children)}
      </li>
    ),

    p: ({ children, ...props }) => <p {...props}>{pc(children)}</p>,
    strong: ({ children, ...props }) => (
      <strong className="tw-font-bold" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em className="tw-italic" {...props}>
        {children}
      </em>
    ),

    code: ({ children, ...props }) => {
      return (
        <code
          className="tw-block tw-bg-neutral-100 tw-rounded tw-p-3 tw-font-mono tw-text-sm tw-overflow-x-auto tw-y-2"
          {...props}
        >
          {children}
        </code>
      )
    },

    a: MarkdownLink,

    blockquote: ({ children, ...props }) => (
      <blockquote className="tw-border-l-4 tw-border-strong tw-pl-4 tw-my-2 tw-italic" {...props}>
        {pc(children)}
      </blockquote>
    ),

    table: ({ children, ...props }) => (
      <div className="tw-overflow-x-auto tw-my-4">
        <table className="tw-min-w-full tw-border-collapse tw-border tw-border-strong" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="tw-bg-neutral-50" {...props}>
        {children}
      </thead>
    ),
    tr: ({ children, ...props }) => (
      <tr className="tw-border-b tw-border-strong" {...props}>
        {children}
      </tr>
    ),
    th: ({ children, ...props }) => (
      <th className="tw-border-r tw-border-strong tw-px-4 tw-py-2 tw-text-left" {...props}>
        {pc(children)}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="tw-border-r tw-border-strong tw-px-4 tw-py-2" {...props}>
        {pc(children)}
      </td>
    ),
  }

  return (
    <div className={`max-w-none ${className || ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
        {text}
      </ReactMarkdown>
    </div>
  )
}

export const MarkdownText = memo(MarkdownTextComponent)
